import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import type { Person, RoleDefinition, StaffingStatus } from "../services/api";

interface PersonFormProps {
  initial?: Partial<Person>;
  availableRoles: RoleDefinition[];
  onSubmit: (payload: Person) => void;
  onCancel?: () => void;
  isSaving?: boolean;
}

const regions = ["US", "INDIA", "LATAM", "EMEA", "APAC"];
const cognitoGroups = ["admin", "ops", "consultant"];
const staffingStatuses: StaffingStatus[] = ["POOL", "BEACH", "ASSIGNED", "HELD"];

export function PersonForm({ initial, availableRoles, onSubmit, onCancel, isSaving }: PersonFormProps) {
  const [form, setForm] = useState<Person>(createInitialState(initial));
  const sortedRoles = useMemo(
    () => [...availableRoles].sort((a, b) => a.label.localeCompare(b.label)),
    [availableRoles],
  );

  useEffect(() => {
    setForm(createInitialState(initial));
  }, [initial]);

  const isEdit = Boolean(initial?.email);

  function handleChange<K extends keyof Person>(field: K, value: Person[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleRolesChange(event: ChangeEvent<HTMLSelectElement>) {
    const selections = Array.from(event.target.selectedOptions).map((option) => option.value);
    handleChange("roles", selections);
  }

function handleSubmit(event: FormEvent) {
  event.preventDefault();
  onSubmit(form);
}

function createInitialState(initial?: Partial<Person>): Person {
  return {
    email: initial?.email ?? "",
    name: initial?.name ?? "",
    region: initial?.region ?? "",
    status: initial?.status ?? "POOL",
    speciality: initial?.speciality ?? "",
    coe_level: initial?.coe_level ?? "",
    notes: initial?.notes ?? "",
    skills: initial?.skills ?? [],
    certifications: initial?.certifications ?? [],
    roles: initial?.roles ?? [],
  };
}

  function handleCancel() {
    setForm(createInitialState(initial));
    onCancel?.();
  }

  return (
    <form className="panel" onSubmit={handleSubmit}>
      <h2>{isEdit ? "Edit Person" : "Add Person"}</h2>
      <div className="form-grid">
        <label>
          Email
          <input
            type="email"
            value={form.email}
            required
            disabled={isEdit}
            onChange={(event) => handleChange("email", event.target.value)}
          />
        </label>
        <label>
          Name
          <input
            type="text"
            value={form.name}
            required
            onChange={(event) => handleChange("name", event.target.value)}
          />
        </label>
        <label>
          Region
          <select value={form.region ?? ""} onChange={(event) => handleChange("region", event.target.value)}>
            <option value="">Select region</option>
            {regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Staffing Status
            <span className="info-icon" title="POOL/BEACH = Available | ASSIGNED = Currently on project | HELD = Reserved for another project">
              ⓘ
            </span>
          </span>
          <select value={form.status ?? "POOL"} onChange={(event) => handleChange("status", event.target.value as StaffingStatus)}>
            {staffingStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label>
          Specialty
          <input type="text" value={form.speciality ?? ""} onChange={(event) => handleChange("speciality", event.target.value)} />
        </label>
        {!initial?.email && (
          <label>
            Role
            <select value={form.coe_level ?? ""} onChange={(event) => handleChange("coe_level", event.target.value)}>
              <option value="">Select group</option>
              {cognitoGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="span-2">
          Roles
          <select
            multiple
            value={form.roles ?? []}
            size={Math.min(8, Math.max(sortedRoles.length, 4))}
            onChange={handleRolesChange}
          >
            {sortedRoles.map((role) => (
              <option key={role.role_code} value={role.role_code}>
                {role.label}
              </option>
            ))}
          </select>
          <p className="helper-text">Select one or more roles that align with the consultant&apos;s experience.</p>
        </label>
        <label className="span-2">
          Notes
          <textarea value={form.notes ?? ""} rows={3} onChange={(event) => handleChange("notes", event.target.value)} />
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" className="primary" disabled={isSaving}>
          {isSaving ? "Saving..." : isEdit ? "Update" : "Create"}
        </button>
        {onCancel && (
          <button type="button" onClick={handleCancel} className="ghost">
            Close
          </button>
        )}
      </div>
    </form>
  );
}
